import { useState } from 'react';
import { REVIEW_NOTE_MIN, type Version } from '@mybusinessmate/schema';
import { Button } from '../ui/button';
import { Dialog } from '../ui/dialog';
import { TextArea } from '../ui/text-area';
import { useToast } from '../ui/toast';
import { useRejectVersion } from '../../lib/tasks-api';

/** The reason a lead gives for sending a version back: the author reads it, the client never does. */
export function RequestChangesDialog({
  open,
  onClose,
  taskId,
  version,
}: {
  open: boolean;
  onClose: () => void;
  taskId: string;
  version: Version;
}) {
  const toast = useToast();
  const reject = useRejectVersion(taskId);
  const [notes, setNotes] = useState('');
  const [touched, setTouched] = useState(false);
  const short = notes.trim().length < REVIEW_NOTE_MIN;
  const author = version.authorName?.split(' ')[0] ?? 'the author';

  const send = () => {
    setTouched(true);
    if (short) return;
    reject.mutate(
      { subId: version.id, notes: notes.trim() },
      {
        onSuccess: () => {
          toast({ title: `Sent v${version.versionNumber} back to ${author}`, tone: 'success' });
          onClose();
        },
        onError: () => toast({ title: 'Couldn’t send it back', description: 'Please try again.', tone: 'error' }),
      },
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      density="form"
      title={`Ask for changes on v${version.versionNumber}`}
      description={`Goes back to ${author}. The client keeps seeing what they have.`}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={send} loading={reject.isPending}>
            Request changes
          </Button>
        </>
      }
    >
      <TextArea
        label={`Note for ${author} (internal)`}
        variant="filled"
        rows={4}
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        placeholder={`Say what should change (at least ${REVIEW_NOTE_MIN} characters). Only staff see it.`}
        error={touched && short ? `Say what should change (at least ${REVIEW_NOTE_MIN} characters)` : undefined}
      />
    </Dialog>
  );
}
